import { ApiError } from './errors'

/**
 * access_token lives 15 minutes; refresh_token lives 30 days and **rotates** —
 * every /refresh returns a new one that must be stored (docs/frontend/README.md §Tokens).
 */
export interface AuthTokens {
  accessToken: string
  refreshToken: string
  expiresIn?: number
}

export interface TokenStore {
  get(): AuthTokens | null
  set(tokens: AuthTokens | null): void
}

export interface TokenPayload {
  access_token: string
  refresh_token: string
  expires_in?: number
}

/** Maps the backend payload onto AuthTokens, failing loudly on a broken shape. */
export function parseTokens(payload: unknown): AuthTokens {
  const p = payload as Partial<TokenPayload> | null
  if (!p || typeof p.access_token !== 'string' || typeof p.refresh_token !== 'string') {
    throw new ApiError({ code: 'internal', status: 0, message: 'malformed token payload' })
  }
  return { accessToken: p.access_token, refreshToken: p.refresh_token, expiresIn: p.expires_in }
}

export function createMemoryTokenStore(initial: AuthTokens | null = null): TokenStore {
  let tokens = initial
  return {
    get: () => tokens,
    set: (next) => {
      tokens = next
    },
  }
}

/** localStorage-backed store. A corrupted entry reads as "logged out", never throws. */
export function createStorageTokenStore(key: string, storage: Storage): TokenStore {
  return {
    get: () => {
      const raw = storage.getItem(key)
      if (!raw) return null
      try {
        return JSON.parse(raw) as AuthTokens
      } catch {
        return null
      }
    },
    set: (next) => {
      if (next) {
        storage.setItem(key, JSON.stringify(next))
        return
      }
      storage.removeItem(key)
    },
  }
}
